import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, QueryList, ViewChild, ViewChildren, AfterContentInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { CourseCardComponent } from '../course-card/course-card.component';
import { MatMenuModule } from '@angular/material/menu';
import { ThreeDotsComponent } from '../three-dots/three-dots.component';
import { CourseCardType } from '../../enums/courseCardType.enum';
import { Category } from '../../models/category';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import { PipesModule } from '../../pipes/pipes.module';
import { AuthService } from '../../services/auth/auth.service';
import { Course } from '../../models/course';
import { ToasterService } from '../../services/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { ToasterTypeEnum } from '../../enums/toasterType.enum';
import { ThreeDotsActionsEnum } from '../../enums/threeDotsActions.enum';
import { Router } from '@angular/router';
import { CourseService } from '../../services/course/course.service';
import { CategoryService } from '../../services/category/category.service';
import { MatDialog } from '@angular/material/dialog';
import { GenericAlertPopupComponent } from '../generic-alert-popup/generic-alert-popup.component';
import { ChangeQuizPercentagePopupComponent } from '../change-quiz-percentage-popup/change-quiz-percentage-popup.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { ManageClientsModalComponent } from '../manage-clients-modal/manage-clients-modal.component';
import { RoleEnum } from '../../enums/role.enum';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { LanguageCategoryService } from '../../services/language-category/language-category.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ImageComponent } from '../image/image.component';
import { DynamicImageDirective } from '../../directives/dynamic-images/dynamic-image.directive';
import { IMAGES_RESOLUTIONS } from '../../utils/image-config';
import { AssignedCategoryService } from '../../services/assigned-category/assigned-category.service';
import { User } from '../../models/user';
import { LanguageCourseService } from '../../services/language-course/language-course.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
	selector: 'app-category-item',
	standalone: true,
	templateUrl: './category-item.component.html',
	styleUrl: './category-item.component.scss',
	imports: [
		CommonModule,
		MatExpansionModule,
		MatChipsModule,
		MatIconModule,
		MatButtonModule,
		CourseCardComponent,
		MatMenuModule,
		ThreeDotsComponent,
		PipesModule,
		TooltipDirective,
		MatTooltipModule,
		ImageComponent,
		DynamicImageDirective,
		NgxSkeletonLoaderModule
	]
})
export class CategoryItemComponent implements OnInit, AfterContentInit, OnChanges {

	@ViewChild('containerCard') containerCard!: ElementRef;
	@ViewChildren('smallCard') smallCards!: QueryList<ElementRef>;
	@ViewChild(ThreeDotsComponent) threeDotsComponent: ThreeDotsComponent;

	private authService = inject(AuthService)
	private courseService = inject(CourseService)
	private categoryService = inject(CategoryService)
	private assignedCategoryService = inject(AssignedCategoryService)
	private languageCategoryService = inject(LanguageCategoryService)
	private languageCourseService = inject(LanguageCourseService)
	private toasterService = inject(ToasterService)
	private spinnerService = inject(SpinnerService)
	private translateService = inject(TranslateService)
	private router = inject(Router);
	private dialog = inject(MatDialog);
	private cdRef = inject(ChangeDetectorRef);

	@Input() model: Category;
	@Input() isAssigned: boolean = false;
	@Input() firstItem: boolean = false;
	@Output() dataOnChange = new EventEmitter<boolean>();
	@Output() eventDuplicate = new EventEmitter<boolean>();

	threeDotsActions = ThreeDotsActionsEnum;
	panelOpenState = false;
	courseCardType = CourseCardType;
	publishStatusEnum = PublishStatusEnum;
	courses: Course[] = [];
	totalQuestions = 0;
	questionsLoading = true;
	assignedClients: User[] = [];
	showAll = false;
	showButton = false;
	userRole: RoleEnum;
	userRoleEnum = RoleEnum;
	translationsActive: boolean = false;
	RESOLUTIONS = IMAGES_RESOLUTIONS

	async ngOnInit(): Promise<void> {
		this.userRole = this.authService.auth.user.role;
		await Promise.all([
			this.loadCourses(),
			this.loadAssignedClients()
		]);
		if (this.firstItem) {
			this.panelOpenState = true;
		}
	}
	async loadCourses() {
		if (!this.model?.id) return;
		this.courses = await this.courseService.getByCategory(this.model, this.authService.projectId);
		this.courses.sort((a, b) => a.index - b.index);
		await this.loadQuestionCount();
	}

	private async loadQuestionCount() {
		this.questionsLoading = true;
		const projectId = this.authService.projectId;
		const defaultLangCourses = await Promise.all(
			this.courses.map(course => this.languageCourseService.getDefault(course.id, projectId))
		);

		this.totalQuestions = defaultLangCourses.reduce((total, langCourse) => {
			const chapters = langCourse?.chapters ?? [];
			const questions = chapters.reduce(
				(sum, chapter) => sum + (chapter.quiz?.questions?.length ?? 0),
				0
			);
			return total + questions;
		}, 0);
		this.questionsLoading = false;
	}

	async loadAssignedClients() {
		if (!this.model?.id) return;
		this.assignedClients = await this.assignedCategoryService.getAssignedClientsOnCategory(this.model.id, this.authService.projectId);
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		const modelChange = changes['model'];
		if (modelChange && modelChange.currentValue?.id !== modelChange.previousValue?.id) {
			await Promise.all([
				this.loadCourses(),
				this.loadAssignedClients()
			]);
			this.updateThreeDotsComponent();
		}
	}

	private updateThreeDotsComponent() {
		if (this.threeDotsComponent) {
		  this.threeDotsComponent.initializeOptions();
		  this.cdRef.detectChanges();
		}
	}

	ngAfterContentInit() {
		setTimeout(() => {
			this.checkIfShowAllButtonIsNeeded();
		}, 500)
	}

	checkIfShowAllButtonIsNeeded() {
		const containerWidth = this.containerCard?.nativeElement?.getBoundingClientRect()?.width;
		let totalSmallCardsWidth = 0;
		const gap = 16;

		this.smallCards?.forEach(card => {
			totalSmallCardsWidth += card?.nativeElement.getBoundingClientRect()?.width + gap;
		});

		totalSmallCardsWidth -= gap;
		if (totalSmallCardsWidth > containerWidth) {
			this.showButton = true;
		} else {
			this.showButton = false;
		}
	}

	toggle(ev: Event) {
		ev.stopPropagation();
		this.panelOpenState = !this.panelOpenState;
	}

	collapse() {
		this.panelOpenState = false;
	}

	async handleAction(ev: ThreeDotsActionsEnum) {
		switch (ev) {
			case this.threeDotsActions.EDIT:
				this.editCategory();
				break;
			case this.threeDotsActions.DELETE:
				this.deleteCategory();
				break
			case this.threeDotsActions.DUPLICATE:
				await this.duplicateCategory();
				break
			case this.threeDotsActions.PUBLISH:
				await this.publishCategory();
				break
			case this.threeDotsActions.MANAGE_CLIENTS:
				 this.manageClients();
				break
			case this.threeDotsActions.ADD_TRANSLATIONS:
				 this.addTranslation();
				break
			case this.threeDotsActions.CHANGE_QUIZ_PERCENTAGE:
				this.changeQuizPercentage();
				break
			default:
				break;
		}
	}

	manageClients() {
		const dialogRef = this.dialog.open(ManageClientsModalComponent, {
			maxWidth: '70vw',
			width: '50vw',
			minWidth: '960px',
			minHeight: '15vw',
			data: {
				title: this.model.name,
				acceptBtnText: 'general.save',
				dismissBtnText: 'general.cancel',
				category: this.model,
				btnColor: 'primary',
			}
		});
		dialogRef.afterClosed().subscribe(async result => {
			this.dataOnChange.emit(true)
		});
	}

	editCategory() {
		this.router.navigate([`/category-creation/${this.model.id}`]);
	}

	changeQuizPercentage() {
		const dialogRef = this.dialog.open(ChangeQuizPercentagePopupComponent, {
			maxWidth: '30vw',
			minHeight: '15vw',
			data: {
				categoryName: this.model?.name,
				passPercentage: this.model?.passPercentage ?? null
			}
		});
		dialogRef.afterClosed().subscribe(async result => {
			if (!result) {
				return;
			}
			await this.categoryService.updateQuizPassPercentage(this.model.id, result.passPercentage, this.authService.projectId);
			this.model.passPercentage = result.passPercentage;
			this.toasterService.show('general.change_quiz_percentage_success', ToasterTypeEnum.SUCCESS);
			this.dataOnChange.emit(true);
		});
	}

	async publishCategory() {
		await this.categoryService.publish(this.model, this.authService.projectId);
		this.dataOnChange.emit(true)
		this.toasterService.show('general.category_published_successfully', ToasterTypeEnum.SUCCESS);
	}


	async duplicateCategory() {
		try {
			this.spinnerService.show();
			const authUser = { id: this.authService.auth?.user?.id, role: this.authService.auth?.user?.role };
			await this.categoryService.duplicate(this.model, this.authService.projectId, authUser);
			if (this.isAssigned) {
				this.showDuplicateDialog();
			} else {
				this.showSuccessToast();
			}
			this.dataOnChange.emit(true);
		} catch (error) {
			console.error('Error duplicating category:', error);
			this.toasterService.show(this.translateService.instant('general.error_occurred'), ToasterTypeEnum.ERROR);
		} finally {
			this.spinnerService.hide();
		}
	}

	private showSuccessToast(){
		this.toasterService.show(this.translateService.instant('general.category_duplicated_successfully'), ToasterTypeEnum.SUCCESS);
	}

	private showDuplicateDialog() {
		const dialogRef = this.dialog.open(GenericAlertPopupComponent, {
			maxWidth: '30vw',
			minHeight: '15vw',
			data: {
				title: this.model.name + this.translateService.instant('category-creation.has_been_duplicated_title'),
				description: this.translateService.instant('category-creation.has_been_duplicated_title_description'),
				acceptBtnText: this.translateService.instant('category-creation.view_my_categories'),
				dismissBtnText: this.translateService.instant('general.cancel'),
				btnColor: 'primary'
			}
		});
		dialogRef.afterClosed().subscribe(async result => {
			if (result) {
				this.eventDuplicate.emit(true);
			}
		});

	}

	addTranslation(){
		this.router.navigate([`/category-creation/${this.model.id}/translations`]);
	}

	deleteCategory() {
		const dialogRef = this.dialog.open(GenericAlertPopupComponent, {
			maxWidth: '30vw',
			minHeight: '15vw',
			data: {
				title: 'general.delete_category',
				description: 'general.delete_category_warning',
				acceptBtnText: 'general.delete',
				dismissBtnText: 'general.keep_it',
				btnColor: 'danger'
			}
		});
		dialogRef.afterClosed().subscribe(async result => {
			if (result) {
				await this.categoryService.deleteById(this.model, this.authService.projectId, this.authService.auth?.user?.id);
				this.dataOnChange.emit(true)
				this.toasterService.show('general.category_deleted_successfully', ToasterTypeEnum.SUCCESS);
			}
		});
	}

	goToPreviewCourse(course: Course) {
		this.router.navigate([`categories/course-preview/${this.model?.id}/${course?.id}`]);
	}

	copyId(e: Event, id: string) {
		e.stopPropagation();
		navigator.clipboard.writeText(id)
		this.toasterService.show('general.copy_id', ToasterTypeEnum.SUCCESS);
	}

	toggleShowAll() {
		this.showAll = !this.showAll;
	}
}
